import React, { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tabs } from '@bb-ui/react-library/dist/components/Tabs';
import { Tab } from '@bb-ui/react-library/dist/components/Tab';
import { TabPanel } from '@bb-ui/react-library/dist/components/TabPanel';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { createStyles, makeStyles } from '@material-ui/core';
import { ITabProps } from '@bb-ui/react-library/dist/components/Tab/Tab.types';
import { useStyles } from './Settings.styles';
import { ResponsiveBox } from '../ResponsiveBox/ResponsiveBox';
import { MainHeader } from '../MainHeader/MainHeader';
import { ActionAreaButtons } from '../ActionAreaButtons/ActionAreaButtons';
import { useHelpLinks } from '../../hooks/useHelpLinks';
import { SnowflakeAccountSettings } from './SnowflakeAccountSettings/SnowflakeAccountSettings';
import { GlobalSettings } from './GlobalSettings/GlobalSettings';
import { useApplicationTitle } from '../../hooks/useApplicationTitle';
import { useUser } from '../../hooks/useUser';
import { GlobalSettingsDialog } from './GlobalSettings/GlobalSettingsDialog/GlobalSettingsDialog';
import { Frame } from '../Frame/Frame';
import { UnavailableTooltip } from '../RequiresApi/UnavailableTooltip';
import { Access, useAccessControl } from '../../hooks/useAccessControl';
import { AuthenticationTab } from './Authentication/AuthenticationTab';
import { UserList } from './User/UserListPage/UserList';

const useTabStyles = makeStyles(() =>
    createStyles({
        alwaysHover: {
            pointerEvents: 'visible !important' as any,
        },
    }),
);

const TabRequiringApi = (
    props: ITabProps & {
        tabNum: number;
        setInitialTab: (tabNum: number) => void;
        access: Access[];
        tabRef?: (el: HTMLElement | null) => void;
        tabIndex?: number;
    },
) => {
    const {
        access,
        tabNum,
        setInitialTab,
        tabRef,
        tabIndex: propsTabIndex,
        ...tabProps
    } = props;
    const styles = useTabStyles();

    const noAccess = access.filter(acc => acc.ok).length === 0;

    const unavailable =
        access.filter(acc => !acc.ok && acc.reason === 'REGION_UNAVAILABLE')
            .length === access.length;

    React.useEffect(() => {
        if (access.filter(acc => acc.ok).length) {
            setInitialTab(tabNum);
        }
    }, [setInitialTab, access, tabNum]);

    if (noAccess && !unavailable) {
        // the tabs are auto-indexed even when tabIndex is set
        // so it's not possible to set the intial position without a placeholder tab
        return <Tab style={{ display: 'none' }} />;
    }

    return (
        <UnavailableTooltip placement="bottom" show={unavailable}>
            <span style={{ display: 'inline-grid' }}>
                <Tab
                    {...tabProps}
                    id={`tab-${tabNum}`}
                    aria-controls={`tabpanel-${tabNum}`}
                    label={tabProps.label}
                    className={styles.alwaysHover}
                    disabled={unavailable}
                    ref={tabRef as any}
                    tabIndex={propsTabIndex}
                >
                    {tabProps.children}
                </Tab>
            </span>
        </UnavailableTooltip>
    );
};

/**
 * The Authentication tab has a URL of its own so it can be linked to, bookmarked
 * and reached with the back button. The other tabs are still the bare /settings
 * path; only this one was asked for, and giving every tab a route would change
 * URLs people already have.
 */
const AUTHENTICATION_TAB = 3;
const AUTHENTICATION_PATH = '/settings/authentication';
const SETTINGS_PATH = '/settings';

export function Settings() {
    const classes = useStyles();
    const { t } = useTranslation();
    const bbUser = useUser();
    const access = useAccessControl();

    const history = useHistory();
    const location = useLocation();

    useApplicationTitle(t('settings.title'));

    const getHelpLink = useHelpLinks();

    /*
     * /settings and /settings/authentication are two exact routes, each keyed
     * by its own path in the app's Switch, so moving between them unmounts this
     * page and mounts a fresh one. The selected tab is component state and does
     * not survive that: a fresh Settings falls back to the first accessible tab,
     * which is how leaving Authentication for any other tab landed on License
     * Management. The clicked tab travels in the history entry instead, which
     * the router keeps across the swap and the back button restores.
     */
    const requestedTab = (location.state as { tabIndex?: number } | undefined)
        ?.tabIndex;
    const initialTabRef = React.useRef<number | undefined>(requestedTab);
    const [tabIndex, setTabIndex] = React.useState<number | undefined>(
        requestedTab,
    );

    const setInitialTab = React.useCallback((newVal: number) => {
        if (initialTabRef.current === undefined) {
            initialTabRef.current = newVal;
            setTabIndex(newVal);
        }
    }, []);

    // The URL selects the tab, not just on first render: the back button changes
    // the path without remounting, and the tab has to follow it.
    const authenticationAllowed =
        access['developer/settings/authentication'].ok;
    React.useEffect(() => {
        if (
            location.pathname.replace(/\/$/, '') === AUTHENTICATION_PATH &&
            authenticationAllowed
        ) {
            initialTabRef.current = AUTHENTICATION_TAB;
            setTabIndex(AUTHENTICATION_TAB);
        }
    }, [location.pathname, authenticationAllowed]);

    const [nextTabIndex, setNextTabIndex] = React.useState(0);
    const [nextPath, setNextPath] = useState('');
    const [modifiedStateChanged, setModifiedStateChanged] = useState(false);
    const [modifiedStateChangedTabChange, setModifiedStateChangedTabChange] =
        useState(false);
    const [
        modifiedStateChangedLeavingPage,
        setModifiedStateChangedLeavingPage,
    ] = useState(false);

    const tabRefs = React.useRef<Array<HTMLElement | null>>([]);

    const handleTabChange = (
        event: ChangeEvent<unknown> | React.KeyboardEvent | any,
        index: number,
    ): boolean => {
        if (modifiedStateChanged && !modifiedStateChangedTabChange) {
            // To stay on the same tab before user decides to cancel or save changes
            setNextTabIndex(index);
            setModifiedStateChangedTabChange(true);
            return false;
        }
        const path =
            index === AUTHENTICATION_TAB ? AUTHENTICATION_PATH : SETTINGS_PATH;
        if (location.pathname.replace(/\/$/, '') !== path) {
            // The other route remounts this page on the clicked tab; a <Prompt> may refuse it
            history.push(path, { tabIndex: index });
            return false;
        }
        setTabIndex(index as number);
        return true;
    };

    // Mirrors the visibility logic in TabRequiringApi: a tab is hidden only when
    // the user has no access AND the reason is not REGION_UNAVAILABLE.
    const isTabVisible = (accesses: Access[]) => {
        const noAccess = accesses.filter(acc => acc.ok).length === 0;
        const unavailable =
            accesses.filter(
                acc => !acc.ok && acc.reason === 'REGION_UNAVAILABLE',
            ).length === accesses.length;
        return !noAccess || unavailable;
    };

    const visibleTabIndices = (
        [
            [0, [access['developer/settings/user-management']]],
            [
                1,
                [
                    access['developer/settings/service-users'],
                    access['developer/settings/credits'],
                ],
            ],
            [2, [access['developer/settings/tenant-settings']]],
            [AUTHENTICATION_TAB, [access['developer/settings/authentication']]],
        ] as [number, Access[]][]
    )
        .filter(([, accesses]) => isTabVisible(accesses))
        .map(([index]) => index);

    const handleTabKeyDown = (event: React.KeyboardEvent) => {
        const currentIndex = tabIndex ?? initialTabRef.current ?? 0;
        if (!visibleTabIndices.length) {
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            const currentVisibleIndex = visibleTabIndices.indexOf(currentIndex);
            const nextIndex =
                currentVisibleIndex === -1
                    ? visibleTabIndices[0]
                    : visibleTabIndices[
                          (currentVisibleIndex + 1) % visibleTabIndices.length
                      ];
            const changed = handleTabChange(event, nextIndex);
            if (changed) {
                tabRefs.current[nextIndex]?.focus();
            }
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            const currentVisibleIndex = visibleTabIndices.indexOf(currentIndex);
            const prevIndex =
                currentVisibleIndex === -1
                    ? visibleTabIndices[visibleTabIndices.length - 1]
                    : visibleTabIndices[
                          (currentVisibleIndex - 1 + visibleTabIndices.length) %
                              visibleTabIndices.length
                      ];
            const changed = handleTabChange(event, prevIndex);
            if (changed) {
                tabRefs.current[prevIndex]?.focus();
            }
        }
    };

    const pageContent: {
        tabs?: React.JSX.Element;
        content?: React.JSX.Element;
    } = {};

    /*
     * Incremental-auth users get a tab bar only once the Authentication tab is
     * available to them, which the static feature flag is the only way to do --
     * the flag service is never queried for their sessions. With the flag off
     * their page is unchanged: no tabs, Snowflake Account Settings as content.
     */
    const activeTabIndex = tabIndex ?? initialTabRef.current ?? 0;
    const feedback =
        bbUser?.isUniversalUser() && activeTabIndex === AUTHENTICATION_TAB
            ? {
                  featureId: 'auth-management' as const,
                  featureNameKey: 'settings.authentication.title',
              }
            : undefined;

    if (bbUser?.isUniversalUser() || authenticationAllowed) {
        pageContent.tabs = (
            <Tabs
                value={tabIndex ?? initialTabRef.current ?? false}
                onChange={(e: any, i: number) => handleTabChange(e, i)}
                onKeyDown={handleTabKeyDown}
                className={classes.tabs}
                orientation="horizontal"
                /*
                 * Not "scrollable". That variant renders a ScrollbarSize
                 * listener which sets an inline negative marginBottom on the
                 * scroller to hide the horizontal scrollbar. Harmless on one
                 * row; once the tabs wrap it pulls the panel up over the second
                 * row, and a click there lands on whatever is painted on top --
                 * which is how pressing Global Settings opened License
                 * Management. Wrapping is what we want here, so the variant
                 * that exists to avoid wrapping is the wrong one.
                 */
                variant="standard"
            >
                <TabRequiringApi
                    tabNum={0}
                    label={t('settings.licenseManagement.title')}
                    data-testid="licenseManagementTab"
                    access={[access['developer/settings/user-management']]}
                    setInitialTab={setInitialTab}
                    tabRef={el => (tabRefs.current[0] = el)}
                    tabIndex={activeTabIndex === 0 ? 0 : -1}
                />
                <TabRequiringApi
                    tabNum={1}
                    label={
                        /* prettier-ignore */
                        <p>
                            <Trans i18nKey="settings.userManagement.title">
                                <span lang="en">Snowflake</span> Account Settings
                            </Trans>
                        </p>
                    }
                    data-testid="snowflakeAccountSettingsTab"
                    access={[
                        access['developer/settings/service-users'],
                        access['developer/settings/credits'],
                    ]}
                    setInitialTab={setInitialTab}
                    tabRef={el => (tabRefs.current[1] = el)}
                    tabIndex={activeTabIndex === 1 ? 0 : -1}
                />
                <TabRequiringApi
                    tabNum={2}
                    label={t('settings.globalSettings.title')}
                    data-testid="globalSettingsTab"
                    access={[access['developer/settings/tenant-settings']]}
                    setInitialTab={setInitialTab}
                    tabRef={el => (tabRefs.current[2] = el)}
                    tabIndex={activeTabIndex === 2 ? 0 : -1}
                />
                <TabRequiringApi
                    tabNum={3}
                    label={t('settings.authentication.title')}
                    data-testid="authenticationTab"
                    access={[access['developer/settings/authentication']]}
                    setInitialTab={setInitialTab}
                    tabRef={el => (tabRefs.current[3] = el)}
                    tabIndex={activeTabIndex === 3 ? 0 : -1}
                />
            </Tabs>
        );

        pageContent.content = (
            <TabPanel
                panelId={`tabpanel-${activeTabIndex}`}
                ariaLabelledby={`tab-${activeTabIndex}`}
            >
                {activeTabIndex === 0 && <UserList />}
                {activeTabIndex === 1 && (
                    <SnowflakeAccountSettings key="snowflakeSettings" />
                )}
                {activeTabIndex === 2 && (
                    <GlobalSettings
                        key="globalSettings"
                        onModifiedStateChanged={setModifiedStateChanged}
                    />
                )}
                {activeTabIndex === 3 && (
                    <AuthenticationTab key="authentication" />
                )}
            </TabPanel>
        );
    } else {
        pageContent.content = (
            <>
                <Typography
                    variant="h2"
                    component="h2"
                    className={classes.title}
                    data-testid="snowflakeAccountSettings"
                >
                    {/* prettier-ignore */}
                    <Trans i18nKey="settings.userManagement.title">
                        <span lang="en">Snowflake</span> Account Settings
                    </Trans>
                </Typography>
                <SnowflakeAccountSettings key="snowflakeSettings" />
            </>
        );
    }

    return (
        <>
            <MainHeader title={t('settings.title')} />
            <ResponsiveBox>
                <Frame className={classes.paper}>
                    {pageContent.tabs}
                    {pageContent.content}
                </Frame>
                <ActionAreaButtons
                    helpLink={getHelpLink('settings')}
                    feedback={feedback}
                />
            </ResponsiveBox>

            <Prompt
                when={modifiedStateChanged && !modifiedStateChangedLeavingPage}
                message={location => {
                    setModifiedStateChangedLeavingPage(true);
                    setNextPath(location.pathname);
                    return false;
                }}
            />
            {/* Navigation to another page */}
            {modifiedStateChangedLeavingPage && tabIndex === 2 && (
                <GlobalSettingsDialog
                    onClose={() => {
                        setModifiedStateChanged(false);
                        setModifiedStateChangedLeavingPage(false);
                        history.push(nextPath);
                    }}
                    onStay={() => {
                        setModifiedStateChangedLeavingPage(false);
                    }}
                />
            )}
            {/* Navigation to another tab within the page */}
            {modifiedStateChangedTabChange && tabIndex === 2 && (
                <GlobalSettingsDialog
                    onClose={() => {
                        setModifiedStateChanged(false);
                        setModifiedStateChangedTabChange(false);
                        setTabIndex(nextTabIndex);
                    }}
                    onStay={() => {
                        setModifiedStateChangedTabChange(false);
                    }}
                />
            )}
        </>
    );
}
